import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Megaphone, ChevronDown, ChevronRight, Info, ExternalLink, Leaf } from 'lucide-react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { PLATFORM_LABELS, CHANNEL_LABELS, attributionLeadsQuery, shortId } from '../utils/adAttribution';

// "Which ads bring people in?" — campaign → ad rows counted like Ads
// Manager's Results: every form submission / ad-referred DM in the window
// is one result, dated when it happened. A returning person who comes back
// through a new campaign is still one lead, but the new campaign gets the
// result. Won / revenue are credited once per lead, to their latest ad.
// Meta rows show real names; Google / TikTok show IDs.

const PRESETS = [
  { id: '7d',  label: 'Last 7 days',  days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
  { id: 'ytd', label: 'Year to date', days: null },
  { id: 'all', label: 'All time',     days: null },
];

function rangeFor(preset) {
  const to = new Date();
  if (preset === 'ytd') return { from: new Date(to.getFullYear(), 0, 1), to };
  if (preset === 'all') return { from: new Date('2000-01-01'), to };
  const days = PRESETS.find((p) => p.id === preset)?.days || 30;
  return { from: new Date(to.getTime() - days * 86400000), to };
}

const PLATFORM_PILL = {
  meta: 'bg-blue-50 text-blue-700 border-blue-200',
  google: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  tiktok: 'bg-gray-900 text-white border-gray-900',
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
};

const Stat = ({ label, value, hint }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-4">
    <div className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
      {label}
      {hint && <span title={hint}><Info size={11} className="text-gray-300" /></span>}
    </div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

const NameCell = ({ name, id, kind }) => {
  if (name) return <span className="text-gray-900 font-medium break-words">{name}</span>;
  if (id) return <span className="font-mono text-[11px] text-gray-500" title={`${kind} ID ${id}`}>{kind} {shortId(id)}</span>;
  return <span className="text-gray-400">—</span>;
};

// "12 · 3 returning" — distinct people behind the results.
const PeopleCell = ({ row }) => (
  <span className="whitespace-nowrap">
    {row.uniqueLeads || 0}
    {row.returningLeads > 0 && (
      <span className="text-[10px] text-gray-400 ml-1" title={`${row.newLeads || 0} new, ${row.returningLeads} already in the CRM`}>
        · {row.returningLeads} returning
      </span>
    )}
  </span>
);

const AdAttributionReport = () => {
  const [preset, setPreset] = useState('30d');
  const [platform, setPlatform] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const { from, to } = rangeFor(preset);

  const { data, isLoading, isError } = useQuery(
    ['reports', 'ad-attribution', preset, platform],
    () => api.get('/reports/ad-attribution', {
      params: { from: from.toISOString(), to: to.toISOString(), ...(platform ? { platform } : {}) },
    }).then((r) => r.data),
    { keepPreviousData: true }
  );

  const campaigns = useMemo(() => data?.campaigns || [], [data]);
  const totals = data?.totals || {};
  const byPlatform = data?.byPlatform || [];
  const attributedShare = data?.totalLeadsInRange
    ? Math.min(100, Math.round((totals.newLeads || 0) / data.totalLeadsInRange * 100))
    : 0;

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const maxResults = useMemo(() => Math.max(1, ...campaigns.map((c) => c.results)), [campaigns]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={28} /> Ad Attribution
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Results per campaign and ad, counted like Ads Manager, and how those people convert.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700"
          >
            <option value="">All platforms</option>
            <option value="meta">Meta Ads</option>
            <option value="google">Google Ads</option>
            <option value="tiktok">TikTok Ads</option>
          </select>
          <div className="flex flex-wrap gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                  preset === p.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-sm text-red-700">Could not load the report. Try again.</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
          <Megaphone size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 text-sm font-medium">No ad results in this range.</p>
          <p className="text-gray-500 text-xs mt-2 max-w-md mx-auto">
            Leads from Meta Lead Ads, Click-to-Messenger / Instagram / WhatsApp ads, Google Ads lead forms and TikTok
            Instant Forms appear here automatically. Older Meta leads may need a one-time sync under
            {' '}<Link to="/inbound-settings" className="text-primary-600 hover:underline">Inbound Channels → Sync ad names</Link>.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Results" value={totals.results || 0}
              hint="Form submissions and ad-referred messages in this range, one per submission (Ads Manager's Results). A person who submits twice counts twice." />
            <Stat label="People" value={totals.uniqueLeads || 0}
              hint={`${totals.newLeads || 0} new, ${totals.returningLeads || 0} returning (already in the CRM). New ad leads are ${attributedShare}% of the ${data?.totalLeadsInRange || 0} leads created in this range.`} />
            <Stat label="Won" value={totals.won || 0} hint="Counted once per person, on the last ad they came through. Win rate: Won ÷ (Won + Lost)." />
            <Stat label="Revenue" value={formatCurrency(totals.revenue || 0)} hint="Estimated value of won leads, credited to the last ad each person came through." />
          </div>

          {byPlatform.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {byPlatform.map((p) => (
                <div key={p.platform} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${PLATFORM_PILL[p.platform]}`}>
                  <span className="font-semibold">{PLATFORM_LABELS[p.platform]}</span>
                  <span>{p.results} results</span>
                  <span>· {p.won} won</span>
                  <span>· {formatCurrency(p.revenue)}</span>
                </div>
              ))}
            </div>
          )}

          {totals.unnamedAds > 0 && !platform && (
            <div className="mb-4 flex flex-wrap items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>
                {totals.unnamedAds === 1 ? '1 ad shows' : `${totals.unnamedAds} ads show`} only an ID. For Meta leads, run
                {' '}<Link to="/inbound-settings" className="font-medium underline">Sync ad names</Link> under Inbound Channels to pull campaign and ad names.
                Google and TikTok only deliver IDs.
              </span>
            </div>
          )}

          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Campaign / Ad</th>
                    <th className="text-right px-3 py-2.5 font-medium" title="Form submissions and ad-referred messages (Ads Manager's Results)">Results</th>
                    <th className="text-right px-3 py-2.5 font-medium hidden sm:table-cell" title="Distinct people. New = first time in the CRM; returning people were already leads.">People</th>
                    <th className="text-right px-3 py-2.5 font-medium hidden lg:table-cell">Open</th>
                    <th className="text-right px-3 py-2.5 font-medium">Won</th>
                    <th className="text-right px-3 py-2.5 font-medium hidden md:table-cell">Win rate</th>
                    <th className="text-right px-3 py-2.5 font-medium">Revenue</th>
                    <th className="text-right px-3 py-2.5 font-medium hidden lg:table-cell">Pipeline</th>
                    <th className="text-right px-3 py-2.5 font-medium hidden md:table-cell">Last result</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.map((c) => {
                    const key = `${c.platform}|${c.campaignId || (c.isOrganic ? 'organic' : 'unknown')}`;
                    const open = expanded.has(key);
                    const leadsHref = `/leads?${c.campaignId
                      ? attributionLeadsQuery({ platform: c.platform, campaignId: c.campaignId })
                      : attributionLeadsQuery({ platform: c.platform })}`;
                    return (
                      <React.Fragment key={key}>
                        <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggle(key)}>
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2 min-w-0">
                              {open ? <ChevronDown size={14} className="mt-1 text-gray-400 shrink-0" /> : <ChevronRight size={14} className="mt-1 text-gray-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${PLATFORM_PILL[c.platform]}`}>
                                    {PLATFORM_LABELS[c.platform]}
                                  </span>
                                  {c.isOrganic ? (
                                    <span className="inline-flex items-center gap-1 text-gray-700 font-medium"><Leaf size={12} className="text-green-600" /> Organic form shares</span>
                                  ) : !c.campaignId && !c.campaignName ? (
                                    <span className="text-gray-600" title="These leads came from an ad whose campaign Meta didn't report (Click-to-Messenger / WhatsApp referrals only carry the ad). Expand to see the ad.">
                                      Campaign not reported
                                    </span>
                                  ) : (
                                    <NameCell name={c.campaignName} id={c.campaignId} kind="Campaign" />
                                  )}
                                </div>
                                <div className="mt-1 h-1 rounded bg-gray-100 w-40 max-w-full">
                                  <div className="h-1 rounded bg-primary-500" style={{ width: `${Math.round(c.results / maxResults * 100)}%` }} />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-gray-900">{c.results}</td>
                          <td className="px-3 py-3 text-right text-gray-600 hidden sm:table-cell"><PeopleCell row={c} /></td>
                          <td className="px-3 py-3 text-right text-gray-600 hidden lg:table-cell">{c.open}</td>
                          <td className="px-3 py-3 text-right text-gray-900">{c.won}</td>
                          <td className="px-3 py-3 text-right text-gray-600 hidden md:table-cell">{c.winRate}%</td>
                          <td className="px-3 py-3 text-right text-gray-900">{formatCurrency(c.revenue)}</td>
                          <td className="px-3 py-3 text-right text-gray-600 hidden lg:table-cell">{formatCurrency(c.pipelineValue)}</td>
                          <td className="px-3 py-3 text-right text-gray-500 hidden md:table-cell whitespace-nowrap">{fmtDate(c.lastResultAt)}</td>
                          <td className="px-3 py-3 text-right">
                            <Link to={leadsHref} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline whitespace-nowrap" title="Show these leads">
                              <ExternalLink size={12} /><span className="hidden sm:inline">Leads</span>
                            </Link>
                          </td>
                        </tr>
                        {open && c.ads.map((a) => {
                          const adKey = `${a.adsetId || ''}|${a.adId || ''}|${a.isOrganic}`;
                          const adHref = `/leads?${a.adId
                            ? attributionLeadsQuery({ platform: a.platform, adId: a.adId })
                            : a.adsetId
                              ? attributionLeadsQuery({ platform: a.platform, adsetId: a.adsetId })
                              : attributionLeadsQuery({ platform: a.platform })}`;
                          const channels = (a.channels || []).filter((ch) => ch && ch !== 'lead_form').map((ch) => CHANNEL_LABELS[ch] || ch);
                          return (
                            <tr key={adKey} className="bg-gray-50/60">
                              <td className="pl-10 pr-4 py-2.5">
                                <div className="flex flex-col gap-0.5 min-w-0">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    {a.isOrganic && !a.adId ? (
                                      <span className="text-gray-600 text-xs">No ad — organic submission</span>
                                    ) : (
                                      <NameCell name={a.adName} id={a.adId} kind="Ad" />
                                    )}
                                    {channels.map((ch) => (
                                      <span key={ch} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500">{ch}</span>
                                    ))}
                                  </div>
                                  {(a.adsetName || a.adsetId) && (
                                    <div className="text-[11px] text-gray-500">
                                      Ad set: {a.adsetName || <span className="font-mono">{shortId(a.adsetId)}</span>}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right text-gray-900">{a.results}</td>
                              <td className="px-3 py-2.5 text-right text-gray-600 hidden sm:table-cell"><PeopleCell row={a} /></td>
                              <td className="px-3 py-2.5 text-right text-gray-600 hidden lg:table-cell">{a.open}</td>
                              <td className="px-3 py-2.5 text-right text-gray-900">{a.won}</td>
                              <td className="px-3 py-2.5 text-right text-gray-600 hidden md:table-cell">{a.winRate}%</td>
                              <td className="px-3 py-2.5 text-right text-gray-900">{formatCurrency(a.revenue)}</td>
                              <td className="px-3 py-2.5 text-right text-gray-600 hidden lg:table-cell">{formatCurrency(a.pipelineValue)}</td>
                              <td className="px-3 py-2.5 text-right text-gray-500 hidden md:table-cell whitespace-nowrap">{fmtDate(a.lastResultAt)}</td>
                              <td className="px-3 py-2.5 text-right">
                                <Link to={adHref} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline whitespace-nowrap" title="Show leads from this ad">
                                  <ExternalLink size={12} /><span className="hidden sm:inline">Leads</span>
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 text-sm font-semibold text-gray-900">
                  <tr>
                    <td className="px-4 py-2.5">Total</td>
                    <td className="px-3 py-2.5 text-right">{totals.results}</td>
                    <td className="px-3 py-2.5 text-right hidden sm:table-cell"><PeopleCell row={totals} /></td>
                    <td className="px-3 py-2.5 text-right hidden lg:table-cell">{totals.open}</td>
                    <td className="px-3 py-2.5 text-right">{totals.won}</td>
                    <td className="px-3 py-2.5 text-right hidden md:table-cell">{totals.winRate}%</td>
                    <td className="px-3 py-2.5 text-right">{formatCurrency(totals.revenue)}</td>
                    <td className="px-3 py-2.5 text-right hidden lg:table-cell">{formatCurrency(totals.pipelineValue)}</td>
                    <td className="hidden md:table-cell"></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 mt-3">
            Results are dated by submission, like Ads Manager: a returning person who submits a new form counts for the new
            campaign on that day. Won / lost / revenue reflect each person's current stage and are credited once, to the last ad
            they came through. Meta names come from the Lead Ads submission; Click-to-Messenger, Instagram and WhatsApp results
            carry the ad headline.
          </p>
        </>
      )}
    </div>
  );
};

export default AdAttributionReport;
